import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useTranslation } from 'react-i18next';

import { ROUTES } from 'navigation/constants';
import colors from 'theme/emotion/colors';
import { INDUSTRIES, ONBOARDING_STEPS } from '../../constants';
import { setUserData, setRegistrationStep } from '../../slice';
import { getUserData } from '../../selectors';
import type { Industry } from '../../types';
import {
  OnboardingBackButton,
  OnboardingActionButton,
  OnboardingButtonSection,
  OnboardingContainer,
  OnboardingScrollContainer,
  OnboardingContentSection,
  OnboardingTitleSection,
  OnboardingTitle,
  OnboardingDescription,
} from '../shared';

const SelectSection = styled('div')({
  width: '100%',
  maxWidth: '28rem',
});

const StyledFormControl = styled(FormControl)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.neutral.white,
    borderRadius: '0.75rem',
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.25)',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary.light,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: colors.primary.light,
    },
  },
});

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    paddingTop: '0.875rem',
    paddingBottom: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
});

const IndustryOption = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '1rem',
  fontFamily: 'Poppins, sans-serif',
});

const EmojiSpan = styled('span')({
  fontSize: '1.2rem',
  minWidth: '1.5rem',
});

interface IndustrySelectionProps {}

const IndustrySelection: React.FC<IndustrySelectionProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userData = useSelector(getUserData);
  const [selectedIndustry, setSelectedIndustry] = useState<string>(userData.industry || '');

  const isValid = selectedIndustry !== '';

  const handleIndustryChange = (industryId: string) => {
    setSelectedIndustry(industryId);
  };

  const handleContinue = () => {
    if (isValid) {
      // Send the industry title (not ID) to match iOS behavior
      const industryTitle = getIndustryTitle(selectedIndustry);
      dispatch(setUserData({ industry: industryTitle }));
      dispatch(setRegistrationStep(ONBOARDING_STEPS.ROLE as any));
      navigate(ROUTES.ONBOARDING.ROLE.path);
    }
  };

  const handleBack = () => {
    navigate(ROUTES.ONBOARDING.NAME.path);
  };

  const getIndustryTitle = (industryId: string): string => {
    const industry = INDUSTRIES.find(ind => ind.id === industryId);
    return industry?.title || '';
  };

  return (
    <OnboardingContainer>
      <OnboardingBackButton onClick={handleBack} />
      
      <OnboardingScrollContainer>
        <OnboardingContentSection>
          <OnboardingTitleSection>
            <OnboardingTitle>{t('onboarding.selectIndustry')}</OnboardingTitle>
            <OnboardingDescription>
              {t('onboarding.selectIndustryDescription')}
            </OnboardingDescription>
          </OnboardingTitleSection>

          <SelectSection>
            <StyledFormControl variant="outlined">
              <InputLabel id="industry-select-label">{t('onboarding.industry')}</InputLabel>
              <StyledSelect
                labelId="industry-select-label"
                value={selectedIndustry}
                onChange={(e) => handleIndustryChange(e.target.value as string)}
                label={t('onboarding.industry')}
              >
                {INDUSTRIES.map((industry: Industry) => (
                  <MenuItem key={industry.id} value={industry.id}>
                    <IndustryOption>
                      {industry.emoji && (
                        <EmojiSpan>{industry.emoji}</EmojiSpan>
                      )}
                      <span>{industry.title}</span>
                    </IndustryOption>
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          </SelectSection>
        </OnboardingContentSection>
      </OnboardingScrollContainer>

      <OnboardingButtonSection>
        <OnboardingActionButton
          disabled={!isValid}
          onClick={handleContinue}
        >
          {t('onboarding.continue')}
        </OnboardingActionButton>
      </OnboardingButtonSection>
    </OnboardingContainer>
  );
};

export default IndustrySelection; 